import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  Stack,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import FavoriteIcon from '@mui/icons-material/Favorite';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import AirIcon from '@mui/icons-material/Air';
import ShieldIcon from '@mui/icons-material/Shield';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { BarChart } from '@mui/x-charts/BarChart';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';

// Hardcoded
const DATES = ['08-02', '08-03', '08-04', '08-05', '08-06', '08-07', '08-08'];

const SLEEP_DATA = {
  deep: [78, 65, 90, 70, 85, 60, 82],
  rem: [95, 88, 102, 80, 98, 75, 100],
  light: [210, 195, 225, 200, 215, 180, 220],
  awake: [18, 22, 12, 25, 15, 30, 14],
};

const HRV_DATA = [42, 38, 47, 35, 44, 33, 46];
const HEART_RATE_DATA = [58, 61, 57, 63, 59, 64, 56];
const BREATHING_DATA = [14.2, 15.1, 13.8, 14.9, 14.0, 15.4, 13.6];
const WEAR_COMPLIANCE = [96, 88, 100, 72, 91, 60, 94];

const CARDIAC_EVENTS = [
  { time: '2026-08-06T02:14:00', rhythmType: 'Irregular rhythm', confidence: 'High confidence' },
  { time: '2026-08-04T23:47:00', rhythmType: 'Irregular rhythm', confidence: 'Medium confidence' },
];

const KPIS = {
  wearPct: 94,
  restingHr: 56,
  rmssd: 46,
  sleepEfficiency: 91,
  breathingRate: 13.6,
};

const IDENTIFIED_DATA_REQUEST_FORM_URL = 'https://googleforms.com/REPLACE_WITH_ACTUAL_FORM_ID';


function KpiCard({ icon, label, value, unit, color }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3, '&:last-child': { pb: 3 } }}>
        <Avatar sx={{ bgcolor: `${color}22`, color, width: 44, height: 44 }}>{icon}</Avatar>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {value}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
              {unit}
            </Typography>
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function TrendCard({ label, value, unit, max, data, color }) {
  return (
    <Card sx={{ borderRadius: 3, bgcolor: '#14181C', color: '#fff', height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="overline" sx={{ color: 'grey.500', letterSpacing: 1, fontSize: 11 }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, lineHeight: 1.1 }}>
          {value}
          <Typography component="span" variant="body2" sx={{ color: 'grey.500', ml: 0.5 }}>
            {unit}
          </Typography>
        </Typography>
        {max != null && (
          <Typography variant="caption" sx={{ color: 'grey.500', display: 'block', mt: 0.5 }}>
            Max {max} {unit}
          </Typography>
        )}
        <Box sx={{ mt: 2.5 }}>
          <SparkLineChart data={data} height={80} area color={color} curve="natural" showHighlight showTooltip />
        </Box>
      </CardContent>
    </Card>
  );
}

function WearRibbon() {
  const max = Math.max(...WEAR_COMPLIANCE);
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, mb: 4 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Wear-time compliance
        </Typography>
        <Stack direction="row" spacing={0.75}>
          {DATES.map((d, i) => (
            <Box key={d} sx={{ flex: 1, textAlign: 'center' }}>
              <Box
                title={`${d}: ${WEAR_COMPLIANCE[i]}%`}
                sx={{
                  height: 40,
                  borderRadius: 1,
                  bgcolor: 'primary.main',
                  // floor raised so low-compliance days stay clearly visible
                  opacity: 0.4 + (WEAR_COMPLIANCE[i] / max) * 0.6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700 }}>
                  {WEAR_COMPLIANCE[i]}%
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {d}
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

const WearableDeviceDashboard = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState('7d');

  const handleRequestIdentifiedData = () => {
    window.open(IDENTIFIED_DATA_REQUEST_FORM_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box sx={{ bgcolor: '#F5F7F8', minHeight: '100vh', p: { xs: 2, md: 4 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/researcher')}
        sx={{
          color: 'text.secondary',
          textTransform: 'none',
          mb: 2,
          '&:hover': { backgroundColor: 'rgba(80, 0, 0, 0.06)', color: '#500000' },
        }}
      >
        Back to Researcher Dashboard
      </Button>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={2} mb={4}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Wearable Device Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            De-identified overview &middot; All participants &middot; Sleep &amp; cardiac wearable data
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <ToggleButtonGroup size="small" value={range} exclusive onChange={(e, v) => v && setRange(v)}>
            <ToggleButton value="7d">7 days</ToggleButton>
            <ToggleButton value="30d">30 days</ToggleButton>
            <ToggleButton value="90d">90 days</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={<LockOpenIcon />}
            onClick={handleRequestIdentifiedData}
            sx={{
              backgroundColor: '#500000',
              borderRadius: '20px',
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: '#4a0000' },
            }}
          >
            Request Identified Data
          </Button>
        </Stack>
      </Stack>


      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <KpiCard icon={<ShieldIcon fontSize="small" />} label="Wear compliance" value={KPIS.wearPct} unit="%" color="#2F6F6B" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <KpiCard icon={<FavoriteIcon fontSize="small" />} label="Resting HR" value={KPIS.restingHr} unit="bpm" color="#E0645A" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <KpiCard icon={<MonitorHeartIcon fontSize="small" />} label="HRV (RMSSD)" value={KPIS.rmssd} unit="ms" color="#7C6FDB" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <KpiCard icon={<BedtimeIcon fontSize="small" />} label="Sleep efficiency" value={KPIS.sleepEfficiency} unit="%" color="#3E8FD0" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <KpiCard icon={<AirIcon fontSize="small" />} label="Breathing rate" value={KPIS.breathingRate} unit="br/min" color="#E8A33D" />
        </Grid>
      </Grid>

      <WearRibbon />

      {/* Trend cards — dark sparkline style */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TrendCard label="Average HRV" value={KPIS.rmssd} unit="ms" max={Math.max(...HRV_DATA)} data={HRV_DATA} color="#9C93EE" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TrendCard label="Resting Heart Rate" value={KPIS.restingHr} unit="bpm" max={Math.max(...HEART_RATE_DATA)} data={HEART_RATE_DATA} color="#F08A7E" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TrendCard label="Breathing Rate" value={KPIS.breathingRate} unit="br/min" max={Math.max(...BREATHING_DATA)} data={BREATHING_DATA} color="#6BB4E8" />
        </Grid>
      </Grid>

      {/* Sleep stages */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12 }}>
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Sleep stages
              </Typography>
              <BarChart
                height={300}
                margin={{ top: 50, left: 60, right: 20, bottom: 40 }}
                xAxis={[{ scaleType: 'band', data: DATES }]}
                series={[
                  { data: SLEEP_DATA.deep, label: 'Deep', stack: 'sleep', color: '#4C3F91' },
                  { data: SLEEP_DATA.rem, label: 'REM', stack: 'sleep', color: '#7C6FDB' },
                  { data: SLEEP_DATA.light, label: 'Light', stack: 'sleep', color: '#B7B0E8' },
                  { data: SLEEP_DATA.awake, label: 'Awake', stack: 'sleep', color: '#E8A33D' },
                ]}
                slotProps={{
                  legend: { direction: 'row', position: { vertical: 'top', horizontal: 'middle' } },
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Flagged cardiac rhythm bouts
          </Typography>
          <List disablePadding>
            {CARDIAC_EVENTS.map((ev, i) => (
              <React.Fragment key={i}>
                <ListItem
                  disableGutters
                  sx={{ py: 1.5 }}
                  secondaryAction={<Chip icon={<WarningAmberIcon />} label={ev.confidence} size="small" color="warning" variant="outlined" />}
                >
                  <ListItemText primary={ev.rhythmType} secondary={new Date(ev.time).toLocaleString()} />
                </ListItem>
                {i < CARDIAC_EVENTS.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WearableDeviceDashboard;